<template>
  <div class="teacher-card">
    <!-- 卡片图片区 -->
    <div class="card-img">
      <img v-if="teacher.avatarUrl" :src="teacher.avatarUrl" :alt="teacher.name" class="teacher-avatar" />
      <div v-else class="default-avatar">
        <span class="default-avatar-text">{{ teacher.name ? teacher.name.charAt(0) : '?' }}</span>
      </div>
      <!-- 课程/学员角标 -->
      <div class="course-badge" v-if="teacher.courseCount !== undefined">
        <div class="badge-item">
          <span class="badge-num">{{ teacher.courseCount }}</span>
          <span class="badge-label">课程</span>
        </div>
        <div class="badge-divider"></div>
        <div class="badge-item">
          <span class="badge-num">{{ teacher.studentCount }}</span>
          <span class="badge-label">学员</span>
        </div>
      </div>
    </div>
    
    <!-- 卡片内容区 -->
    <div class="card-content">
      <!-- 名字和性别放在同一行 -->
      <div class="name-wrapper">
        <div class="teacher-name">{{ teacher.name }}</div>
        <!-- 使用 Element UI 图标 -->
        <span class="gender-badge" :class="genderClass">
          <i :class="genderIcon"></i>
          <span class="gender-text">{{ genderText }}</span>
        </span>
      </div>
      <div class="teacher-title" v-if="teacher.title">
        {{ teacher.title }}
      </div>
      <div class="teacher-bio">{{ teacher.bio }}</div>

      <!-- 资质等级 -->
      <div class="teacher-quals" v-if="teacher.qualifications && teacher.qualifications.length > 0">
        <span class="qual-tag" v-for="(q, i) in teacher.qualifications" :key="i">
          {{ q.name }}<template v-if="q.level">·{{ q.level }}教练</template>
        </span>
      </div>

      <!-- 专长标签 -->
      <div class="expertise">
        <span v-for="(skill, index) in teacher.expertise" :key="index">{{ skill }}</span>
      </div>
      
      <!-- 统计信息 -->
      <div class="teacher-stats">
        <div class="stat" v-for="(stat, index) in teacher.stats" :key="index">
          <i :class="stat.icon"></i> {{ stat.text }}
        </div>
      </div>
      
      <!-- 按钮 -->
      <div>
        <button class="btn-sm" :disabled="disabled" :class="{ 'btn-disabled': disabled }" @click="handleBook">
          {{ teacher.buttonText }}
          <i v-if="teacher.buttonIcon" :class="teacher.buttonIcon"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TeacherCard',
  props: {
    teacher: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    genderClass() {
      if (this.teacher.gender === 1) return 'gender-male'
      if (this.teacher.gender === 2) return 'gender-female'
      return ''
    },
    // 使用 Element UI 图标
    genderIcon() {
      if (this.teacher.gender === 1) return 'el-icon-male'
      if (this.teacher.gender === 2) return 'el-icon-female'
      return 'el-icon-user'
    },
    genderText() {
      if (this.teacher.gender === 1) return '男'
      if (this.teacher.gender === 2) return '女'
      return ''
    }
  },
  methods: {
    handleBook() {
      // 将整个教师对象传递给父组件
      this.$emit('book', this.teacher)
    }
  }
}
</script>

<style scoped>
/* 名字和性别容器 */
.name-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.teacher-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a5a36;
  line-height: 1.4;
}

/* 性别标签样式 */
.gender-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
}

/* 男性样式 */
.gender-male {
  background-color: #e6f7ed;
  color: #1a5a36;
}

/* 女性样式 */
.gender-female {
  background-color: #fce4ec;
  color: #c2185b;
}

.gender-badge i {
  font-size: 0.7rem;
}

.gender-text {
  line-height: 1;
}

.teacher-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #f1f5f9;
}

.default-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6f7ed;
  color: #1a5a36;
}

.default-avatar-text {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #1a5a36;
  color: #fff;
  font-size: 2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.teacher-quals {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0;
}

.qual-tag {
  display: inline-flex;
  align-items: center;
  background: #e6f7ed;
  padding: 3px 10px;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #1a5a36;
  border: 1px solid #c5edd0;
}

.course-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  color: #fff;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  backdrop-filter: blur(4px);
}

.badge-item {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.badge-num {
  font-weight: 700;
  font-size: 0.85rem;
}

.badge-label {
  font-size: 0.6rem;
  opacity: 0.85;
}

.badge-divider {
  width: 1px;
  height: 14px;
  background: rgba(255,255,255,0.35);
}

.course-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.course-stat-num {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a5a36;
  line-height: 1.2;
}

.course-stat-label {
  font-size: 0.65rem;
  color: #64748b;
}

.course-stat-divider {
  width: 1px;
  height: 24px;
  background: #e2e8f0;
}
</style>